import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPorAsins } from "./porAsinListSlice";
import { RootState } from "../app/rootReducer";
import {
  Table,
  CollectionPreferences,
  Pagination,
  Header,
} from "@amzn/awsui-components-react/polaris";
import { COLUMN_DEFINITIONS } from "./PorAsinListTable-config";
import {
  setPreferences,
  pageSelectorOptions,
  setSort,
  tableColumnDefinitions,
  setAsinPageNumber,
  mergeVisibleContentPreferences,
} from "../porAsinPageControl/porAsinPageControl";
import { fetchEditableAttributes } from "../porAsinList/porAsinListSlice";
import { WEBSITE_TITLE } from "../../constants/common";
import { SortFacet } from "../../from-server/asin-central/search";
import { getEditColumnDefs } from "./asinListEdit-config";
import {
  defaultSort,
  EmptyTableState,
  TableHeaderMessage,
  EditActionButtons,
  ViewActionButtons,
} from "./TableCommonElements";
import { writeToLocalStorage } from "../../util/localStorageUtils";
import { trackComponentView } from "../telemetry/telemetry";
import { loadFromUrl } from "../searchSideBar/searchSideBarConnector";
import { isNoFilterApplied } from "../searchSideBar/queryStringHelpers";
import { NoFiltersPanel } from "../validation/NoFiltersPanel";
import logger from "../../art-logger-config";

export const PorAsinListTable = () => {
  const dispatch = useDispatch();
  const {
    currentPagePorAsins,
    isLoading,
    count,
    isEditing,
    editablePorAsins,
    isGettingEditableAttributes,
    editableAttributes,
  } = useSelector((state: RootState) => state.porAsinList);
  const {
    queryString,
    activePropertyFilters,
    activePropertyFiltersVisibilityEnabled,
  } = useSelector((state: RootState) => state.generateQuery);
  const { preferences, sort, descending, pageNumber } = useSelector(
    (state: RootState) => state.porAsinPageControl
  );

  const isDefaultView = useMemo(() => {
    const queryFromUrl = window.location.href.split("#")[1];
    return (
      queryString === "(all)" &&
      (queryFromUrl === undefined || queryFromUrl === "")
    );
  }, [queryString, window]);

  useEffect(() => {
    dispatch(loadFromUrl());
  }, []);

  const getSort = (): SortFacet[] =>
    sort === undefined
      ? defaultSort
      : [
          {
            attributeName: sort?.sortingField,
            ascending: !descending,
          },
        ];

  useEffect(() => {
    // In case we are in default view (fetch (all) but no query string is on route) we set
    if (queryString && !isDefaultView) {
      const offset = preferences.pageSize * (pageNumber - 1);
      dispatch(
        fetchPorAsins(offset, preferences.pageSize, queryString, getSort())
      );
    }
  }, [
    isDefaultView,
    pageNumber,
    queryString,
    preferences.pageSize,
    dispatch,
    sort,
    descending,
  ]);

  useEffect(() => {
    if (activePropertyFiltersVisibilityEnabled) {
      dispatch(
        mergeVisibleContentPreferences({
          activeColumns: Object.keys(activePropertyFilters ?? {}),
        })
      );
    }
  }, [queryString, activePropertyFiltersVisibilityEnabled]);

  useEffect(() => {
    // editableAttributes gates the weblab link in AsinDisplayCell on the DEFAULT
    // (non-edit) view, so it must be loaded regardless of edit mode. Fetch once
    // on mount if it is not already loaded/loading. The empty dependency array
    // replaces the previous no-dep-array effect that dispatched on every render.
    if (!editableAttributes.length && !isGettingEditableAttributes) {
      dispatch(fetchEditableAttributes());
    }
  }, []);

  useEffect(() => {
    trackComponentView("asinCentralTable", "table");
  }, []);

  if (isNoFilterApplied(queryString)) {
    return (
      <div data-testid="por-asin-list-table-wrapper">
        <NoFiltersPanel
          dataNoun="ASIN data"
          onResetPagination={() =>
            dispatch(setAsinPageNumber({ pageNumber: 1 }))
          }
        />
      </div>
    );
  }

  return (
    <div data-testid="por-asin-list-table-wrapper">
      <Table
        data-testid="porAsinListTable"
        header={
          <Header
            data-testid="por-asin-list-header"
            variant="h1"
            description={TableHeaderMessage(
              (pageNumber - 1) * preferences.pageSize,
              currentPagePorAsins?.length ?? 0,
              count
            )}
            actions={isEditing ? <EditActionButtons /> : <ViewActionButtons />}
          >
            {WEBSITE_TITLE}
          </Header>
        }
        loading={isLoading}
        loadingText={"Loading"}
        empty={
          isDefaultView
            ? EmptyTableState(
                "Welcome to Devices ASIN Central! Please use the Search panel and search for specific ASINs or use the filters to get started."
              )
            : EmptyTableState()
        }
        columnDefinitions={
          isEditing ? getEditColumnDefs(editableAttributes) : COLUMN_DEFINITIONS
        }
        items={isEditing ? editablePorAsins : currentPagePorAsins}
        stickyHeader={true}
        sortingColumn={sort}
        sortingDescending={descending}
        onSortingChange={(event) => dispatch(setSort(event.detail))}
        pagination={
          isEditing ? undefined : (
            <Pagination
              data-testid="por-asin-list-pagination"
              pagesCount={Math.ceil(count / preferences.pageSize)}
              currentPageIndex={pageNumber}
              disabled={isLoading}
              onChange={({ detail }) =>
                dispatch(
                  setAsinPageNumber({ pageNumber: detail.currentPageIndex })
                )
              }
            />
          )
        }
        visibleColumns={preferences.visibleContent}
        preferences={
          isEditing ? undefined : (
            <CollectionPreferences
              data-testid="por-asin-list-preferences"
              onConfirm={(event) => {
                writeToLocalStorage(
                  "ASIN_TABLE_PREFERENCES",
                  JSON.stringify(event.detail)
                );
                dispatch(setPreferences(event.detail));
              }}
              title={"Preferences"}
              confirmLabel={"Confirm"}
              cancelLabel={"Cancel"}
              disabled={isLoading}
              preferences={preferences}
              pageSizePreference={pageSelectorOptions}
              visibleContentPreference={tableColumnDefinitions}
            />
          )
        }
        className={"porTable"}
      />
    </div>
  );
};
